import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Info, User, Building2, Download, Mail, Check, Link2, ArrowRight, ChevronDown, Target } from "lucide-react";

const PUBLIC_ORIGIN = "https://usewylo.com";

export const CURRENCIES = { GBP: "£", EUR: "€", USD: "$", CAD: "CA$" };
export const OFF_FOCUS_HOURS = 169;
const DEFAULT_EFFECTIVENESS = 62.5;
const COMMAND_RATIO = 0.27;
const INDIVIDUAL_TIER = { id: "focus", name: "Focus", monthly: 23 };
const ENTERPRISE_TIERS = [
  { id: "focus", name: "Focus", monthly: 23 },
  { id: "sync-web", name: "Sync Web", monthly: 18 },
  { id: "sync", name: "Sync", monthly: 28 },
  { id: "momentum", name: "Momentum", monthly: 38 },
  { id: "command", name: "Command", monthly: null },
];
const PRESET_GROUPS = [
  { group: "By industry", items: [
    { id: "law", name: "Law & professional services", offFocus: 198 },
    { id: "consulting", name: "Consulting", offFocus: 215 },
    { id: "saas", name: "SaaS & technology", offFocus: 243 },
    { id: "finance", name: "Finance & banking", offFocus: 205 },
    { id: "healthcare", name: "Healthcare", offFocus: 185 },
    { id: "public-sector", name: "Public sector", offFocus: 165 },
    { id: "creative-media", name: "Creative & media", offFocus: 225 },
  ] },
  { group: "By function", items: [
    { id: "engineering", name: "Engineering & development", offFocus: 232 },
    { id: "sales", name: "Sales & account management", offFocus: 250 },
    { id: "marketing", name: "Marketing", offFocus: 222 },
    { id: "customer-support", name: "Customer support", offFocus: 258 },
    { id: "operations", name: "Operations & admin", offFocus: 210 },
    { id: "people-hr", name: "People & HR", offFocus: 198 },
  ] },
  { group: "By managerial level", items: [
    { id: "individual-contributor", name: "Individual contributor", offFocus: 178 },
    { id: "team-lead", name: "Team lead / manager", offFocus: 238 },
    { id: "senior-manager", name: "Senior / middle manager", offFocus: 262 },
    { id: "executive", name: "Executive / leadership", offFocus: 288 },
  ] },
];
const ALL_PRESETS = PRESET_GROUPS.flatMap((g) => g.items);

export function InfoHint({ children, testId, className = "" }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const show = () => { clearTimeout(timer.current); setOpen(true); };
  const hide = () => { timer.current = setTimeout(() => setOpen(false), 150); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" onMouseEnter={show} onMouseLeave={hide}
          onClick={() => { clearTimeout(timer.current); setOpen((o) => !o); }}
          className={`inline-flex text-[#2C6BAC] hover:text-[#1A3A5C] align-middle ml-1.5 ${className}`} aria-label="More information" data-testid={testId}>
          <Info size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" className="w-72 text-xs text-[#1A1A2E] leading-relaxed z-[300]" onMouseEnter={show} onMouseLeave={hide} data-testid={`${testId}-content`}>
        {children}
      </PopoverContent>
    </Popover>
  );
}

export const fmt = (n, cur) => `${CURRENCIES[cur]}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) && n >= 0 ? n : 0; };
const n1 = (n) => n.toLocaleString(undefined, { maximumFractionDigits: 1 });

export default function CostCalculator({ compact = false }) {
  const [mode, setMode] = useState("individual");
  const [days, setDays] = useState("220");
  const [hours, setHours] = useState("7.5");
  const [salary, setSalary] = useState("50000");
  const [currency, setCurrency] = useState("GBP");
  const [employees, setEmployees] = useState("5");
  const [tier, setTier] = useState("sync");
  const [presetOn, setPresetOn] = useState(false);
  const [preset, setPreset] = useState("");
  const [presetMenuOpen, setPresetMenuOpen] = useState(false);
  const [effectiveness, setEffectiveness] = useState(DEFAULT_EFFECTIVENESS);
  const enterprise = mode === "enterprise";
  const presetSel = ALL_PRESETS.find((p) => p.id === preset);

  const calc = useMemo(() => {
    const presetObj = ALL_PRESETS.find((p) => p.id === preset);
    const offFocus = presetOn && presetObj ? presetObj.offFocus : OFF_FOCUS_HOURS;
    const contracted = num(days) * num(hours);
    const hourly = contracted > 0 ? num(salary) / contracted : 0;
    const count = enterprise ? Math.max(1, Math.floor(num(employees))) : 1;
    const total = hourly * offFocus * count;
    const tierObj = enterprise ? ENTERPRISE_TIERS.find((t) => t.id === tier) : INDIVIDUAL_TIER;
    const isCommand = enterprise && tier === "command";
    const refocus = isCommand ? total * COMMAND_RATIO : tierObj.monthly * 12 * count;
    const value = total * (effectiveness / 100) - refocus;
    const valuePerEmployee = value / count;
    return { contracted, hourly, count, total, tierObj, isCommand, refocus, value, valuePerEmployee, offFocus, presetObj };
  }, [days, hours, salary, employees, enterprise, tier, presetOn, preset, effectiveness]);

  return (
    <div className={`rounded-xl border border-[#E8ECF2] bg-[#F8F9FC] ${compact ? "p-4 md:p-5" : "p-5 md:p-6"}`} data-testid="cost-calculator">
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-display text-xl font-bold text-[#1A1A2E]" data-testid="calc-title">
          {enterprise ? "Your Company's Numbers" : "Your Individual Numbers"}
        </h3>
        <div className="flex shrink-0 rounded-full border border-[#E8ECF2] bg-white p-1" role="tablist" data-testid="calc-mode-toggle">
          {[["individual", User, "Individual"], ["enterprise", Building2, "Enterprise"]].map(([id, Icon, label]) => (
            <button key={id} type="button" role="tab" aria-selected={mode === id} onClick={() => setMode(id)}
              className={`inline-flex items-center justify-center gap-1.5 text-sm px-3 sm:px-4 py-1.5 rounded-full transition-colors ${mode === id ? "bg-[#1A3A5C] text-white" : "text-[#555568] hover:text-[#1A1A2E]"}`}
              data-testid={`calc-mode-${id}`}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
        <div className="space-y-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold">Your inputs</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Working Days" unit="per Yr" id="calc-days">
              <Input id="calc-days" type="number" min={1} max={366} value={days} onChange={(e) => setDays(e.target.value)} data-testid="calc-days" />
            </Field>
            <Field label="Working Day" unit="hrs per day" id="calc-hours">
              <Input id="calc-hours" type="number" min={0.5} max={24} step={0.5} value={hours} onChange={(e) => setHours(e.target.value)} data-testid="calc-hours" />
            </Field>
          </div>
          <div className={enterprise ? "grid grid-cols-2 gap-3" : ""}>
            <div>
              <Label htmlFor="calc-salary" className="text-xs uppercase tracking-[0.14em] text-[#555568]">
                {enterprise ? "Avg. Salary, Annual" : "User Salary, Annual"}
              </Label>
              <div className="mt-1 flex gap-2">
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger className="w-20 shrink-0 bg-white" data-testid="calc-currency"><SelectValue /></SelectTrigger>
                  <SelectContent className="z-[300]" position="popper">
                    {Object.keys(CURRENCIES).map((c) => <SelectItem key={c} value={c} data-testid={`calc-currency-${c}`}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input id="calc-salary" type="number" min={0} step={500} value={salary} onChange={(e) => setSalary(e.target.value)} className="bg-white" data-testid="calc-salary" />
              </div>
            </div>
            {enterprise && (
              <Field label="Employee Count" unit="" id="calc-employees">
                <Input id="calc-employees" type="number" min={1} value={employees} onChange={(e) => setEmployees(e.target.value)} data-testid="calc-employees" />
              </Field>
            )}
          </div>
          <div className="rounded-lg border border-[#E8ECF2] bg-white px-3 py-2.5">
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="calc-preset-label">
              <Checkbox checked={presetOn} onCheckedChange={(v) => setPresetOn(!!v)} data-testid="calc-preset-toggle" />
              <span className="text-[11px] uppercase tracking-[0.14em] text-[#555568] font-semibold inline-flex items-center">
                Industry presets
                <InfoHint testId="calc-preset-info">Tick this and pick an industry, function or level to auto-fill a typical annual off-focus time for that kind of work. Untick to return to your own numbers.</InfoHint>
              </span>
            </label>
            <Popover open={presetMenuOpen} onOpenChange={(o) => presetOn && setPresetMenuOpen(o)}>
              <PopoverTrigger asChild>
                <button type="button" disabled={!presetOn}
                  className="mt-2 flex h-9 w-full items-center justify-between rounded-md border border-[#E8ECF2] bg-white px-3 text-sm text-left disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="calc-preset-select">
                  <span className={presetSel ? "text-[#1A1A2E] truncate" : "text-[#555568]"}>
                    {presetSel ? `${presetSel.name} · ~${presetSel.offFocus} hrs/yr` : "Choose a profile"}
                  </span>
                  <ChevronDown size={14} className="shrink-0 text-[#555568]" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="z-[300] w-[--radix-popover-trigger-width] p-0" data-testid="calc-preset-menu">
                <Command>
                  <CommandInput placeholder="Search industry, function or level…" className="text-sm" data-testid="calc-preset-search" />
                  <CommandList className="max-h-64">
                    <CommandEmpty>No match found.</CommandEmpty>
                    {PRESET_GROUPS.map((g) => (
                      <CommandGroup key={g.group} heading={g.group}>
                        {g.items.map((p) => (
                          <CommandItem key={p.id} value={p.name} data-testid={`calc-preset-${p.id}`}
                            onSelect={() => { setPreset(p.id); setPresetMenuOpen(false); }}>
                            <span className="flex-1">{p.name}</span>
                            <span className="text-xs text-[#555568] tabular-nums">~{p.offFocus} hrs/yr</span>
                            {preset === p.id && <Check size={14} className="ml-2 text-[#1D9E75]" />}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    ))}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <p className="mt-2 text-[10px] leading-snug text-[#555568]" data-testid="calc-preset-footnote">
              Figures serve as an illustrative estimate.
            </p>
          </div>
        </div>

        <div className="space-y-4 md:pt-[6px]">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold">Worked out for you</p>
          <Computed label={enterprise ? "Average Hourly Rate" : "Hourly Rate"} unit={enterprise ? "per Employee" : ""} value={fmt(calc.hourly, currency)} testId="calc-hourly"
            hint={<>'{enterprise ? "Average Salary" : "User Salary"}' ÷ 'Working Hrs.': {fmt(num(salary), currency)} ÷ {n1(calc.contracted)} = {fmt(calc.hourly, currency)} per hour.</>} />
          <Computed label="Working Hrs." unit="per Yr" value={n1(calc.contracted)} testId="calc-contracted"
            hint={<>'Working Hrs.' means the hours contracted to work by the employer or client base. Calculated as 'Working Day' × 'Working Days': {num(hours)} × {num(days)} = {n1(calc.contracted)} hrs.</>} />
          <Computed label="Avg. Off-Focus Time" unit="Hrs per Yr" value={calc.offFocus} testId="calc-offfocus"
            hint={presetOn && calc.presetObj
              ? <>{calc.presetObj.name} — a typical profile sees ~{calc.offFocus} off-focus hours a year. Untick 'Industry presets' to return to the research baseline.</>
              : "Based on existing research and field tests: two disruptive interruptions a day, each costing ~23 minutes of recovery (Mark et al., 2008)."} />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        <ResultBox tone="navy" testId="calc-total-card"
          title={enterprise ? "Total Off-Focus Cost to Enterprise" : "Total Off-Focus Cost"}
          hint={enterprise
            ? <>'Average Hourly Rate' × 'Avg. Off-Focus Time' × 'Employee Count': {fmt(calc.hourly, currency)} × {calc.offFocus} × {calc.count} = {fmt(calc.total, currency)} per year.</>
            : <>'Hourly Rate' × 'Avg. Off-Focus Time': {fmt(calc.hourly, currency)} × {calc.offFocus} = {fmt(calc.total, currency)} per year.</>}
          figure={fmt(calc.total, currency)} figureTestId="calc-total"
          sub={enterprise ? `${fmt(calc.total / calc.count, currency)} per year per employee` : "per year"} />

        <ResultBox tone="grey" testId="calc-refocus-card"
          title={calc.isCommand ? "Est. Re-Focus Cost" : "Total Refocus Cost"}
          hint={calc.isCommand
            ? "Command pricing is negotiated per contract."
            : enterprise
              ? <>WYLO {calc.tierObj.name} at {CURRENCIES[currency]}{calc.tierObj.monthly}/user/month × 12 × {calc.count} employees = {fmt(calc.refocus, currency)} per year.</>
              : <>WYLO Focus at {CURRENCIES[currency]}{INDIVIDUAL_TIER.monthly}/month × 12 = {fmt(calc.refocus, currency)} per year.</>}
          figure={fmt(calc.refocus, currency)} figureSuffix="per year" figureTestId="calc-refocus"
          control={enterprise && (
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger className="h-8 text-xs bg-white border-[#1A3A5C]/20 w-36" data-testid="calc-tier"><SelectValue /></SelectTrigger>
              <SelectContent className="z-[300]" position="popper">
                {ENTERPRISE_TIERS.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`calc-tier-${t.id}`}>{t.name}{t.monthly ? ` · ${CURRENCIES[currency]}${t.monthly}/user/mo` : " · custom"}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          sub={
            <Link to={`/pricing?tier=${calc.tierObj.id}`} className="inline-flex items-center gap-1 font-semibold text-[#2C6BAC] hover:text-[#1A3A5C]" data-testid="calc-see-included">
              <Target size={12} /> See what's included <ArrowRight size={12} />
            </Link>
          } />

        <ResultBox tone="green" testId="calc-value-card" center
          title="Return On Investment"
          hint={calc.isCommand
            ? <>Assumes WYLO recovers {n1(effectiveness)}% of off-focus time. Drag the slider to model effectiveness between 35% and 90%.</>
            : <>'Off-Focus Cost' × {n1(effectiveness)}% effectiveness - 'Refocus Cost': ({fmt(calc.total, currency)} × {n1(effectiveness)}%) - {fmt(calc.refocus, currency)} = {fmt(calc.value, currency)} per year. Drag the slider to model between 35% and 90%.</>}
          figure={fmt(calc.value, currency)} figureTestId="calc-value"
          sub={enterprise
            ? <>{fmt(calc.valuePerEmployee, currency)} per employee / year</>
            : "per year"}
          footer={
            <div className="w-full">
              <input type="range" min={35} max={90} step={0.5} value={effectiveness}
                onChange={(e) => setEffectiveness(parseFloat(e.target.value))}
                className="w-full h-1.5 cursor-pointer accent-[#1D9E75]"
                data-testid="calc-effectiveness-slider" aria-label="WYLO effectiveness rate" />
              <div className="mt-1.5 flex items-center justify-between text-[10px] text-[#1D9E75]/40">
                <span>35%</span>
                <span className="font-semibold text-[#1D9E75]/55" data-testid="calc-effectiveness-value">{n1(effectiveness)}% effectiveness</span>
                <span>90%</span>
              </div>
            </div>
          } />
      </div>

      <ShareCostCard enterprise={enterprise} calc={calc} currency={currency} salary={num(salary)} days={num(days)} hours={num(hours)} effectiveness={effectiveness} />
    </div>
  );
}

const TONES = {
  navy: { box: "bg-[#1A3A5C] text-white", label: "text-[#E8ECF2]/80", sub: "text-[#E8ECF2]/70", info: "text-white hover:text-[#7DE3B4]" },
  grey: { box: "bg-[#E8ECF2] text-[#1A3A5C]", label: "text-[#1A3A5C]/80", sub: "text-[#1A3A5C]/80", info: "text-[#1A3A5C]" },
  green: { box: "bg-white border border-[#E8ECF2] text-[#1D9E75]", label: "text-[#1D9E75]/90", sub: "text-[#1D9E75]/80", info: "text-[#1D9E75]" },
};

function AutoFitText({ children, max, min = 16, className = "", testId }) {
  const outer = useRef(null);
  const inner = useRef(null);
  const [size, setSize] = useState(max);
  useLayoutEffect(() => {
    const el = outer.current, span = inner.current;
    if (!el || !span) return;
    let raf = 0;
    const measure = () => {
      let s = max, guard = 0;
      span.style.fontSize = s + "px";
      while (span.scrollWidth > el.clientWidth && s > min && guard < 80) { s -= 1; span.style.fontSize = s + "px"; guard++; }
      setSize(s);
    };
    const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };
    schedule();
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [children, max, min]);
  return (
    <p ref={outer} className={`w-full ${className}`} data-testid={testId}>
      <span ref={inner} className="inline-block whitespace-nowrap" style={{ fontSize: `${size}px` }}>{children}</span>
    </p>
  );
}

function ResultBox({ tone, title, hint, figure, figureSuffix, figureTestId, sub, control, footer, testId, center }) {
  const t = TONES[tone];
  return (
    <div className={`rounded-xl p-4 md:p-5 flex flex-col ${center ? "items-center justify-center text-center" : ""} ${t.box}`} data-testid={testId}>
      <p className={`text-[10px] uppercase tracking-[0.18em] font-semibold flex items-center ${center ? "justify-center" : ""} ${t.label}`}>
        {title}
        <InfoHint testId={`${testId}-info`} className={t.info}>{hint}</InfoHint>
      </p>
      {control && <div className="mt-2">{control}</div>}
      <AutoFitText max={center ? 46 : 34} min={16} testId={figureTestId}
        className={`mt-2 font-display font-bold tracking-tight tabular-nums leading-tight ${center ? "text-center" : ""}`}>
        {figure}{figureSuffix && <span className={`ml-1.5 text-sm font-semibold ${t.sub}`}>{figureSuffix}</span>}
      </AutoFitText>
      <div className={`mt-1 text-xs ${t.sub}`}>{sub}</div>
      {footer && <div className="mt-4 w-full">{footer}</div>}
    </div>
  );
}

function roundRect(x, px, py, w, h, r, fill, stroke) {
  x.beginPath();
  x.moveTo(px + r, py); x.lineTo(px + w - r, py); x.quadraticCurveTo(px + w, py, px + w, py + r);
  x.lineTo(px + w, py + h - r); x.quadraticCurveTo(px + w, py + h, px + w - r, py + h);
  x.lineTo(px + r, py + h); x.quadraticCurveTo(px, py + h, px, py + h - r);
  x.lineTo(px, py + r); x.quadraticCurveTo(px, py, px + r, py); x.closePath();
  x.fillStyle = fill; x.fill();
  if (stroke) { x.strokeStyle = stroke; x.lineWidth = 2; x.stroke(); }
}

function fitText(x, text, maxWidth, startSize, weight = "bold") {
  let size = startSize;
  do { x.font = `${weight} ${size}px sans-serif`; size -= 4; } while (x.measureText(text).width > maxWidth && size > 20);
}

function drawCostCard({ enterprise, total, refocus, value, refocusTitle, tierName, count, salary, days, hours, hourly, offFocus, effLabel }) {
  const c = document.createElement("canvas");
  c.width = 1200; c.height = 630;
  const x = c.getContext("2d");
  const g = x.createLinearGradient(0, 0, 1200, 630);
  g.addColorStop(0, "#F8F9FC"); g.addColorStop(1, "#E8ECF2");
  x.fillStyle = g; x.fillRect(0, 0, 1200, 630);
  x.fillStyle = "rgba(44,107,172,0.10)";
  x.beginPath(); x.arc(1080, 60, 260, 0, Math.PI * 2); x.fill();
  roundRect(x, 70, 60, 52, 52, 10, "#1A3A5C");
  x.fillStyle = "#F8F9FC"; x.font = "bold 28px sans-serif"; x.fillText("W", 82, 98);
  x.fillStyle = "#1A1A2E"; x.font = "bold 38px sans-serif"; x.fillText("wylo", 136, 99);
  x.fillStyle = "#2C6BAC"; x.font = "600 20px sans-serif";
  x.fillText(enterprise ? `THE HIDDEN TAX ON FOCUS  ·  OUR TEAM (${count} EMPLOYEES)` : "THE HIDDEN TAX ON FOCUS  ·  MY NUMBERS", 70, 160);
  x.fillStyle = "#1A1A2E";
  const headline = enterprise ? "What interruptions cost us, and what WYLO gives back." : "What interruptions cost me, and what WYLO gives back.";
  fitText(x, headline, 1060, 44);
  x.fillText(headline, 70, 212);

  const boxes = [
    { title: enterprise ? "TOTAL OFF-FOCUS COST TO ENTERPRISE" : "TOTAL OFF-FOCUS COST", figure: total, sub: "per year", bg: "#1A3A5C", fg: "#FFFFFF", muted: "rgba(232,236,242,0.8)" },
    { title: refocusTitle.toUpperCase(), figure: refocus, sub: enterprise ? `WYLO ${tierName} · per year` : "WYLO Focus · per year", bg: "#E8ECF2", fg: "#1A3A5C", muted: "rgba(26,58,92,0.75)", stroke: "#D5DCE6" },
    { title: "RETURN ON INVESTMENT", figure: value, sub: `per year, at ${effLabel} effectiveness`, bg: "#FFFFFF", fg: "#1D9E75", muted: "rgba(29,158,117,0.8)", stroke: "#E8ECF2" },
  ];
  const bw = 340, bh = 230, gap = 20, top = 260;
  boxes.forEach((b, i) => {
    const px = 70 + i * (bw + gap);
    roundRect(x, px, top, bw, bh, 18, b.bg, b.stroke);
    x.fillStyle = b.muted; x.font = "600 15px sans-serif";
    const words = b.title.split(" "); let line = "", ly = top + 38;
    words.forEach((w) => {
      const t = line ? `${line} ${w}` : w;
      if (x.measureText(t).width > bw - 48 && line) { x.fillText(line, px + 24, ly); line = w; ly += 20; } else line = t;
    });
    x.fillText(line, px + 24, ly);
    x.fillStyle = b.fg; fitText(x, b.figure, bw - 48, 52);
    x.fillText(b.figure, px + 24, top + 140);
    x.fillStyle = b.muted; x.font = "17px sans-serif"; x.fillText(b.sub, px + 24, top + 180);
  });

  x.fillStyle = "#555568"; x.font = "18px sans-serif";
  x.fillText(`Assumptions: ${enterprise ? "avg " : ""}salary ${salary} · ${days} days × ${hours} hrs · ${hourly}/hr · ${offFocus} off-focus hrs/yr (Mark et al., 2008)`, 70, 550);
  x.fillStyle = "#2C6BAC"; x.font = "600 18px sans-serif";
  x.fillText("Run your own numbers at usewylo.com/resources  ·  Privacy-first focus for people and teams", 70, 590);
  return c.toDataURL("image/png");
}

const EMAIL_CLIENTS = [
  { id: "default", label: "Default mail app", href: (s, b, to = "", cc = "") => `mailto:${to}?${cc ? `cc=${cc}&` : ""}subject=${s}&body=${b}` },
  { id: "gmail", label: "Gmail (web)", href: (s, b, to = "", cc = "") => `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&cc=${cc}&su=${s}&body=${b}` },
  { id: "outlook", label: "Outlook.com", href: (s, b, to = "", cc = "") => `https://outlook.live.com/mail/0/deeplink/compose?to=${to}&cc=${cc}&subject=${s}&body=${b}` },
  { id: "office", label: "Outlook 365 (work)", href: (s, b, to = "", cc = "") => `https://outlook.office.com/mail/deeplink/compose?to=${to}&cc=${cc}&subject=${s}&body=${b}` },
  { id: "yahoo", label: "Yahoo Mail", href: (s, b, to = "", cc = "") => `https://compose.mail.yahoo.com/?to=${to}&cc=${cc}&subject=${s}&body=${b}` },
];

function ShareCostCard({ enterprise, calc, currency, salary, days, hours, effectiveness }) {
  const [copied, setCopied] = useState(false);
  const total = fmt(calc.total, currency);
  const hourly = fmt(calc.hourly, currency);
  const salaryStr = fmt(salary, currency);
  const effLabel = `${n1(effectiveness)}%`;
  const url = `${PUBLIC_ORIGIN}/resources#calculator`;
  const text = enterprise
    ? `Our organisation loses an estimated ${total} a year to refocusing after interruptions (${calc.count} employees, avg salary ${salaryStr}, ${calc.offFocus} off-focus hrs/yr per person - Mark et al., 2008). WYLO ${calc.tierObj.name} would cost ${fmt(calc.refocus, currency)} a year and, at ${effLabel} effectiveness, return ${fmt(calc.value, currency)} (${fmt(calc.valuePerEmployee, currency)} per employee). Calculated with WYLO.`
    : `I lose an estimated ${total} a year to refocusing after interruptions (salary ${salaryStr}, ${days} days × ${hours} hrs, ${calc.offFocus} off-focus hrs/yr - Mark et al., 2008). WYLO Focus would cost ${fmt(calc.refocus, currency)} a year and, at ${effLabel} effectiveness, return ${fmt(calc.value, currency)} in value. Calculated with WYLO.`;
  const subject = encodeURIComponent(enterprise ? "Our off-focus cost - worth a look" : "My off-focus cost - worth a look");
  const body = encodeURIComponent(`${text}\n\nSee the methodology and run your own numbers: ${url}`);
  const demoSubject = encodeURIComponent(`WYLO demo request - ${calc.count} employees`);
  const demoBody = encodeURIComponent(`Hello WYLO team,\n\nWe'd like to schedule a demo.\n\n${text}\n\nBest regards,`);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch {}
  };
  const download = () => {
    const a = document.createElement("a");
    a.href = drawCostCard({
      enterprise, total, hourly, count: calc.count, salary: salaryStr, days, hours, offFocus: calc.offFocus, effLabel,
      refocus: fmt(calc.refocus, currency), value: fmt(calc.value, currency),
      refocusTitle: calc.isCommand ? "Est. Re-Focus Cost" : "Total Refocus Cost", tierName: calc.tierObj.name,
    });
    a.download = enterprise ? "our-off-focus-cost-wylo.png" : "my-off-focus-cost-wylo.png";
    a.click();
  };
  return (
    <div className="mt-5" data-testid="share-cost-card">
      <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-2">
        {enterprise ? "Share with your leadership team" : "Share your numbers"}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" className="border-[#E8ECF2] bg-white" onClick={download} data-testid="cost-share-download">
          <Download size={14} className="mr-1.5" /> Download card
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="border-[#E8ECF2] bg-white" data-testid="cost-share-email">
              <Mail size={14} className="mr-1.5" /> Email it <ChevronDown size={12} className="ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="z-[300]" data-testid="cost-share-email-menu">
            {EMAIL_CLIENTS.map((c) => (
              <DropdownMenuItem key={c.id} asChild>
                <a href={c.href(subject, body)} target={c.id === "default" ? undefined : "_blank"} rel="noreferrer" data-testid={`cost-share-email-${c.id}`}>{c.label}</a>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" size="sm" className="border-[#E8ECF2] bg-white" onClick={copy} data-testid="cost-share-copy">
          {copied ? <Check size={14} className="mr-1.5 text-[#1D9E75]" /> : <Link2 size={14} className="mr-1.5" />} {copied ? "Copied" : "Copy summary"}
        </Button>
        {enterprise ? (
          <a href={`mailto:demos@usewylo.com?cc=support@usewylo.com&subject=${demoSubject}&body=${demoBody}`} data-testid="calc-cta-demo">
            <Button size="sm" className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white">Schedule a demo <ArrowRight size={14} className="ml-1.5" /></Button>
          </a>
        ) : (
          <Link to="/register" data-testid="calc-cta-trial">
            <Button size="sm" className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white">Start free trial <ArrowRight size={14} className="ml-1.5" /></Button>
          </Link>
        )}
      </div>
    </div>
  );
}

function Field({ label, unit, id, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-[0.14em] text-[#555568]">
        {label} {unit && <span className="normal-case tracking-normal text-[#555568]/70">· {unit}</span>}
      </Label>
      <div className="mt-1 [&_input]:bg-white">{children}</div>
    </div>
  );
}

function Computed({ label, unit, value, hint, testId, small }) {
  return (
    <div className={small ? "flex-1 sm:flex-none" : ""}>
      <p className={`uppercase tracking-[0.14em] text-[#555568] flex items-center ${small ? "text-[10px]" : "text-xs"}`}>
        {label} {unit && <span className="normal-case tracking-normal text-[#555568]/70 ml-1">· {unit}</span>}
        <InfoHint testId={`${testId}-info`}>{hint}</InfoHint>
      </p>
      <p className={`mt-1 flex items-center rounded-md border border-dashed border-[#E8ECF2] bg-white/60 px-3 font-semibold text-[#1A1A2E] tabular-nums ${small ? "h-8 text-xs" : "h-10 text-sm"}`} data-testid={testId}>
        {value}
      </p>
    </div>
  );
}
